import { useTranslation } from 'react-i18next'
import {
  Alert,
  Button,
  InputGroup,
  isValidPercentage,
  Label,
  NumericInputGroup,
  useLatinCharacterValidation
} from '@/common'

function QpdItem({
  name,
  value,
  index,
  itemUnitSuffix,
  itemPlaceholderName,
  onNameChange,
  onValueChange,
  onRemoveOtherElement
}: {
  name: string
  value: number | null
  index: number
  itemUnitSuffix: string
  itemPlaceholderName: string
  onNameChange: (evt: React.ChangeEvent<HTMLInputElement>, index: number) => void
  onValueChange: (evt: React.ChangeEvent<HTMLInputElement>, index: number) => void
  onRemoveOtherElement: (index: number) => void
}) {
  const { t } = useTranslation()

  const { error: nameError } = useLatinCharacterValidation(
    name,
    t('common:errors.latinCharacters'),
    undefined
  )
  return (
    <div key={index} className="grid grid-cols-4 gap-4 items-center">
      <div className="col-span-2">
        <InputGroup
          sizeType="sm"
          value={name}
          placeholder={itemPlaceholderName}
          error={nameError || undefined}
          onChange={evt => onNameChange(evt, index)}
        />
      </div>
      <div className="col-span-1">
        <NumericInputGroup
          sizeType="sm"
          value={value?.toString() || ''}
          onChange={evt => onValueChange(evt, index)}
          suffix={itemUnitSuffix}
          numericFormat={{
            decimalScale: 3,
            isAllowed: isValidPercentage
          }}
        />
      </div>
      <Button sizeType="sm" variant="txt-danger" onClick={() => onRemoveOtherElement(index)}>
        {t('common:actions.remove')}
      </Button>
    </div>
  )
}

export default function QpdWithItemsEditor({
  items,
  hasRangeError,
  onNameChange,
  onValueChange,
  onRemoveOtherElement,
  onAddOtherElement,
  maxItems,
  itemUnitSuffix,
  strings
}: {
  items: { name: string; value: number | null }[]
  hasRangeError: boolean
  onNameChange: (evt: React.ChangeEvent<HTMLInputElement>, index: number) => void
  onValueChange: (evt: React.ChangeEvent<HTMLInputElement>, index: number) => void
  onRemoveOtherElement: (index: number) => void
  onAddOtherElement: () => void
  maxItems: number
  itemUnitSuffix: string
  strings: {
    itemPlaceholderName: string
    label: string
    hint: string
    rangeError: string
    instructions: string
  }
}) {
  const { t } = useTranslation()

  return (
    <>
      <Label className="text-sm font-medium">{strings.label}</Label>
      <p className="text-sm text-text-secondary italic">{strings.hint}</p>
      {!items.length && (
        <Alert type="info" sizeType="sm" className="mt-2">
          {strings.instructions}
        </Alert>
      )}
      {hasRangeError && (
        <Alert type="error" sizeType="sm" className="mt-2">
          {strings.rangeError}
        </Alert>
      )}
      {items.map((item, index) => (
        <QpdItem
          key={index}
          name={item.name}
          value={item.value}
          index={index}
          itemUnitSuffix={itemUnitSuffix}
          itemPlaceholderName={strings.itemPlaceholderName}
          onNameChange={onNameChange}
          onValueChange={onValueChange}
          onRemoveOtherElement={onRemoveOtherElement}
        />
      ))}
      {items.length < maxItems && (
        <div className="space-y-2">
          <Button onClick={onAddOtherElement} sizeType="sm" variant="subtle">
            {t('common:actions.add')}
          </Button>
        </div>
      )}
    </>
  )
}
